import { useCallback, useEffect } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Sun, Moon } from "lucide-react";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { cn } from "@/lib/utils";

export const LightDarkToggle = ({
  hasDarkModeColors,
  canvasId,
  variant = "compact",
  isViewDarkMode: externalIsViewDarkMode,
  setViewDarkMode: externalSetViewDarkMode,
}: {
  hasDarkModeColors: boolean;
  canvasId: string;
  onPaletteDisplayModeChange?: (mode: "light" | "dark") => void;
  variant?: "compact" | "full-width";
  isViewDarkMode?: boolean;
  setViewDarkMode?: (isDark: boolean) => void;
}) => {
  const canvasDarkMode = useDarkModeStore(
    (state) => state.canvasDarkModeStates[canvasId],
  );

  // Use external state if provided, otherwise use global state
  const _isViewDarkMode = useDarkModeStore((state) => state.isViewDarkMode);
  const isViewDarkMode =
    externalIsViewDarkMode !== undefined
      ? externalIsViewDarkMode
      : _isViewDarkMode;

  const setViewDarkMode = useCallback(
    (toSet: boolean) => {
      if (externalSetViewDarkMode) {
        externalSetViewDarkMode(toSet);
      } else {
        useDarkModeStore.getState().setViewDarkMode(toSet);
      }
    },
    [externalSetViewDarkMode],
  );

  // Only sync with global state if not using external state
  useEffect(() => {
    if (externalIsViewDarkMode === undefined && !externalSetViewDarkMode) {
      setViewDarkMode(canvasDarkMode);
    }
  }, [
    setViewDarkMode,
    canvasDarkMode,
    externalIsViewDarkMode,
    externalSetViewDarkMode,
  ]);

  return (
    <>
      <ToggleGroup
        type="single"
        value={isViewDarkMode ? "dark" : "light"}
        onValueChange={(value) => {
          if (value) {
            const newDisplayMode = value as "light" | "dark";
            // showing the generate UI when there are no dark mode colors
            setViewDarkMode(newDisplayMode === "dark");
          }
        }}
        className={cn(
          "bg-[#1f1f22] rounded-md p-0.5",
          variant === "full-width" && "w-full",
        )}
      >
        <ToggleGroupItem
          value="light"
          className={cn(
            "p-0 rounded-md",
            variant === "compact" ? "w-6 h-6" : "flex-1 h-6",
          )}
        >
          <Sun className="w-[14px] h-[14px]" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="dark"
          className={cn(
            "p-0 rounded-md",
            variant === "compact" ? "w-6 h-6" : "flex-1 h-6",
          )}
        >
          <Moon className="w-[14px] h-[14px]" />
        </ToggleGroupItem>
      </ToggleGroup>
    </>
  );
};
